import React, { forwardRef } from "react";
import { ArrowUpDown } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/cn";
import { useDensity } from "../../theme/ThemeContext";
import { Icon } from "../../atoms/Icon/Icon";
import type { Density } from "../../theme/types";

export const TableRow = forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className = "", ...props }, ref) => (
    <tr ref={ref} className={cn("border-b border-border transition-colors hover:bg-surface", className)} {...props} />
  )
);
TableRow.displayName = "TableRow";

const cell = cva("align-middle", {
  variants: {
    density: {
      comfortable: "px-4 py-3",
      compact: "px-3 py-1.5 text-xs",
    },
  },
  defaultVariants: { density: "comfortable" },
});

type TableHeadProps = React.ThHTMLAttributes<HTMLTableCellElement> &
  Omit<VariantProps<typeof cell>, "density"> & {
    density?: Density;
    sortable?: boolean;
    sorted?: "asc" | "desc" | false;
  };

export const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className = "", density, sortable, sorted, children, ...props }, ref) => {
    const resolvedDensity = useDensity(density);
    return (
      <th
        ref={ref}
        className={cn(cell({ density: resolvedDensity }), "text-left font-semibold text-text-muted", className)}
        {...props}
      >
        {sortable ? (
          <button type="button" className="inline-flex items-center gap-1 hover:text-text">
            {children}
            <Icon icon={ArrowUpDown} size="sm" className={sorted ? "text-accent-600" : "text-text-subtle"} />
          </button>
        ) : (
          children
        )}
      </th>
    );
  }
);
TableHead.displayName = "TableHead";

type TableCellProps = React.TdHTMLAttributes<HTMLTableCellElement> &
  Omit<VariantProps<typeof cell>, "density"> & { density?: Density };

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className = "", density, ...props }, ref) => {
    const resolvedDensity = useDensity(density);
    return <td ref={ref} className={cn(cell({ density: resolvedDensity }), className)} {...props} />;
  }
);
TableCell.displayName = "TableCell";
